/* eslint-disable no-nested-ternary */
import { useTranslation } from "react-i18next";
import { v4 as uuid } from "uuid";

import { Company } from "../../../models/company";
import { Destination } from "../../../models/destination";
import { Item } from "../../../models/item";
import { JobMode } from "../../../models/job.mode";
import { Location } from "../../../models/location";
import { Material } from "../../../models/material";
import { QuotedItem, RoomJob } from "../../../models/sales";
import { ServiceBooking } from "../../../models/service.booking";
import { DESTINATION, Mode } from "../../../util/enums";
import formatAllMaterialsFromItem from "../../../util/formatAllMaterialsFromItem";
import { getNextSequenceNumber } from "../../../util/getNextSequenceNumber";
import { isAllied } from "../../../util/isAllied";
import { NotificationToast } from "../Toast/NotificationToast";

interface Props {
  update: (value: RoomJob[]) => void;
  data: RoomJob[];
  jobId: number;
  defaultMode?: string;
}

export const useAction = ({ data, jobId, defaultMode, update }: Props) => {
  const { t: translate } = useTranslation();

  const handleAddRoom = (room: RoomJob) => {
    if (room && room.id) {
      const inventoryUpdated = [...(data || [])];

      const hasSameRoom = inventoryUpdated.filter((roomJobQuote) => {
        const roomName = room.name?.replace(/[0-9]+/g, "").trim();
        const roomJobQuoteName = roomJobQuote?.name?.replace(/[0-9]+/g, "").trim();

        return (
          roomJobQuote?.id === room?.id ||
          roomName?.toLowerCase() === roomJobQuoteName?.toLowerCase()
        );
      });

      if (hasSameRoom.length === 0) {
        update([...data, { ...room, itemsJob: [], materials: [], pictures: [] }]);

        NotificationToast.success(translate("aRoomHasBeenAddedToTheInventory"), {
          id: "aRoomHasBeenAddedToTheInventory",
        });
      } else {
        const roomNumber = getNextSequenceNumber(hasSameRoom.map((sameRoom) => sameRoom.name));

        update([
          ...data,
          {
            name: `${room.name} ${roomNumber}`,
            jobId,
            companyId: room.companyId,
            itemsJob: [],
            materials: [],
            pictures: [],
            roomPictures: [],
            materialsDeleted: [],
            itemsJobDeleted: [],
          },
        ]);

        NotificationToast.success(translate("aRoomHasBeenAddedToTheInventory"), {
          id: "aRoomHasBeenAddedToTheInventory",
        });
      }
    }
  };

  const handleUpdateRoom = (olderRoomName: string, newRoomName: string, indexUpdate: number) => {
    const rooms = data;
    const indexRoomToUpdate = rooms.findIndex((room) => room.name === olderRoomName);
    const roomNames: string[] =
      rooms && rooms.length > 0
        ? rooms.map((room, index) => {
            if (index !== indexUpdate) return room.name.trim() || "";

            return "";
          })
        : [];

    if (indexRoomToUpdate !== -1) {
      if (!roomNames.includes(newRoomName)) {
        const roomsUpdated = [...(rooms || [])];

        roomsUpdated[indexRoomToUpdate] = {
          ...roomsUpdated[indexRoomToUpdate],
          name: newRoomName,
          jobId,
          id: roomsUpdated[indexRoomToUpdate]?.jobId
            ? roomsUpdated[indexRoomToUpdate]?.id
            : undefined,
        };

        update([...roomsUpdated]);
      } else {
        NotificationToast.warning(translate("aRoomWithThisNameAlreadyExists"), {
          id: "aRoomWithThisNameAlreadyExists",
        });
      }
    }
  };

  const getModeByDestination = (destination?: string, defaultMode?: string) => {
    switch (destination?.toLowerCase()) {
      case DESTINATION.ROAD.toLowerCase():
        return Mode.Road;
      case DESTINATION.SEA.toLowerCase():
        return Mode.Sea;
      case DESTINATION.AIR.toLowerCase():
        return Mode.Air;
      default:
        return defaultMode;
    }
  };

  const handleAddItemToRoom = (
    item: Item,
    roomName: string,
    company?: Company,
    destination?: Destination,
    destinationResidence?: Location,
    modeDestination?: JobMode
  ) => {
    if (roomName === "") {
      NotificationToast.warning(translate("youHaveToSelectARoomBeforeAddAnItem"), {
        id: "youHaveToSelectARoomBeforeAddAnItem",
      });

      return;
    }

    const room = data.find((roomItem) => roomItem.name === roomName);

    if (room) {
      if (getModeByDestination(destination?.name, defaultMode)) {
        const roomJobToUpdate = room;

        let itemToAdd: QuotedItem = {
          pictures: [],
          itemJob: {
            description: item.name,
            companyId: item.companyId,
            roomId: room.id,
            volume: item.volume,
            itemId: item.id,
            item,
            quantity: 1,
            weight: item.weight,
            mode:
              isAllied(company) && destinationResidence && destinationResidence?.id
                ? Mode.Road
                : destinationResidence
                ? defaultMode
                : getModeByDestination(destination?.name, defaultMode),
            destination,
            destinationId: destination && destination?.id ? destination?.id : null,
            destinationResidence,
            destinationResidenceId:
              destinationResidence && destinationResidence?.id ? destinationResidence?.id : null,
            modeDestination,
            modeDestinationId: modeDestination && modeDestination?.id ? modeDestination.id : null,
            id: undefined,
            packer: "",
            comments: "",
            uuid: uuid(),
          },
        };

        if (
          item &&
          item?.packingMaterials &&
          item?.packingMaterials?.length > 0 &&
          itemToAdd?.itemJob?.destination?.name?.toLowerCase() !==
            DESTINATION.NOT_GOING?.toLowerCase()
        ) {
          itemToAdd = {
            ...itemToAdd,
            itemJob: {
              ...(itemToAdd?.itemJob || {}),
              packingMaterials: formatAllMaterialsFromItem(
                item?.packingMaterials,
                itemToAdd?.itemJob
              ),
            },
          };
        }

        if (roomJobToUpdate) roomJobToUpdate.itemsJob.unshift(itemToAdd);

        data[data.indexOf(room)] = roomJobToUpdate;

        update([...data]);

        NotificationToast.success(translate("anItemWasAddToTheSelectedRoom"), {
          id: "anItemWasAddToTheSelectedRoom",
        });
      } else {
        NotificationToast.warning(
          translate("youHaveToChooseAQuoteModeToAddAnItem", {
            id: "youHaveToChooseAQuoteModeToAddAnItem",
          })
        );
      }
    }
  };

  const handleDeleteItem = (roomName: string, itemIndex: number) => {
    const rooms = data;
    const roomToUpdate = rooms.find((room) => room.name === roomName);

    if (roomToUpdate && roomToUpdate.itemsJob) {
      const itemsJobDeleted = roomToUpdate?.itemsJobDeleted || [];

      itemsJobDeleted.push(roomToUpdate?.itemsJob[itemIndex]);
      roomToUpdate.itemsJob?.splice(itemIndex, 1);
      roomToUpdate.itemsJobDeleted = itemsJobDeleted;
      update([...rooms]);
    }
  };

  const handleUpdateItem = (roomName: string, itemIndex: number, item: QuotedItem) => {
    const rooms = data;
    const roomToUpdate = rooms.find((room) => room.name === roomName);

    if (roomToUpdate && roomToUpdate.itemsJob)
      roomToUpdate.itemsJob[itemIndex] = {
        ...roomToUpdate.itemsJob[itemIndex],
        ...item,
      };

    update([...rooms]);
  };

  const handleAddMaterialToRoom = (
    roomName: string,
    service: ServiceBooking,
    company?: Company,
    destination?: Destination,
    destinationResidence?: Location,
    modeDestination?: JobMode
  ) => {
    if (roomName === "") {
      NotificationToast.warning(translate("youHaveToSelectARoomBeforeAddAMaterial"), {
        id: "youHaveToSelectARoomBeforeAddAMaterial",
      });

      return;
    }

    const room = data.find((room) => room.name === roomName);

    if (room) {
      if (getModeByDestination(destination?.name, defaultMode)) {
        const roomToAdd: Material = {
          serviceBooking: {
            id: service.id,
            code: service.code,
            companyId: service.companyId,
            isItem: service.isItem,
            name: service.name,
            volume: service.volume,
            price: service?.price,
          },
          quantity: 1,
          price: service.price ? Number(service.price) : 0,
          uuid: uuid(),
          volume: service.volume ? Number(service.volume) : 0,
          roomId: room?.id,
          jobId,
          packingMaterialId: service.id,
          mode:
            isAllied(company) && destinationResidence && destinationResidence?.id
              ? Mode.Road
              : destinationResidence
              ? defaultMode
              : getModeByDestination(destination?.name, defaultMode),
          destination,
          destinationId: destination && destination?.id ? destination?.id : null,
          destinationResidence,
          destinationResidenceId:
            destinationResidence && destinationResidence?.id ? destinationResidence?.id : null,
          modeDestination,
          modeDestinationId: modeDestination && modeDestination?.id ? modeDestination.id : null,
          room: {
            id: room?.id,
            name: room?.name,
            jobId: room?.jobId,
            companyId: room?.companyId,
          } as RoomJob,
        };

        room.materials.unshift(roomToAdd);

        update([...data]);

        NotificationToast.success(translate("aMaterialWasAddToTheSelectedRoom"), {
          id: "aMaterialWasAddToTheSelectedRoom",
        });
      } else {
        NotificationToast.warning(translate("youHaveToChooseAQuoteModeToAddAMaterial"), {
          id: "youHaveToChooseAQuoteModeToAddAMaterial",
        });
      }
    }
  };

  const handleDeleteMaterial = (roomName: string, materialIndex: number) => {
    const rooms = data;
    const roomToUpdate = rooms.find((room) => room.name === roomName);

    if (roomToUpdate) {
      const materialsDeleted = roomToUpdate?.materialsDeleted || [];

      materialsDeleted.push(roomToUpdate.materials[materialIndex]);
      roomToUpdate.materials.splice(materialIndex, 1);
      roomToUpdate.materialsDeleted = materialsDeleted;
      update([...data]);
    }
  };

  const handleUpdateMaterial = (roomName: string, materialIndex: number, material: Material) => {
    const roomToUpdate = data.find((room) => room.name === roomName);

    if (roomToUpdate) roomToUpdate.materials[materialIndex] = material;

    update([...data]);
  };

  const handleAddPicturesToRoom = (roomName: string, pictures: (string | null)[]) => {
    const roomToUpdate = data.find((room) => room.name === roomName);

    if (roomToUpdate) {
      roomToUpdate.pictures = [...pictures, ...(roomToUpdate.pictures || [])];
    }
    update([...data]);
  };

  const handleDeletePicturesFromRoom = (roomName: string, uri?: string) => {
    if (uri) {
      const roomToUpdate = data.find((room) => room.name === roomName);

      if (roomToUpdate) {
        const pictures = roomToUpdate.pictures || [];
        const indexPicture = pictures.indexOf(uri);

        if (indexPicture > -1) {
          delete pictures[indexPicture];

          return;
        }
        const roomPictures = roomToUpdate.roomPictures || [];
        const roomPicturesDeleted = roomToUpdate.roomPicturesDeleted || [];

        roomPictures.forEach((roomPicture, index) => {
          if (roomPicture.mediaLink === uri) {
            roomPicturesDeleted.push(roomPicture);
            delete roomPictures[index];
          }
        });
        roomToUpdate.pictures = pictures.filter(Boolean);
        roomToUpdate.roomPictures = roomPictures.filter(Boolean);
        roomToUpdate.roomPicturesDeleted = roomPicturesDeleted;
        update([...data]);
      }
    }
  };

  return {
    handleAddRoom,
    handleUpdateRoom,
    handleAddItemToRoom,
    handleDeleteItem,
    handleUpdateItem,
    handleAddMaterialToRoom,
    handleDeleteMaterial,
    handleUpdateMaterial,
    handleAddPicturesToRoom,
    handleDeletePicturesFromRoom,
  };
};
